import type { MarketVariants, SiteVariants } from '@/lib/i18n'

export type Slide3UrgencyCopy = {
  title: string
  /** Sender label shown in caps before the message body. Must be a real-feeling
   *  bank fraud team name in the user's market. */
  sender: string
  /** First half of the message body, the threat / urgency text. */
  threatBody: string
  /** The phishing URL shown as a link. Has three pieces because the middle
   *  segment (`brandToken`) is highlighted under the "gain trust" beat while
   *  the surrounding pieces (`urlBefore` / `urlAfter`) highlight under
   *  "push action". */
  urlBefore: string
  brandToken: string
  urlAfter: string

  /** Pattern-breakdown text. Each row matches the pattern step labels. */
  trustItem: string
  pressureFear: string
  pressureUrgency: string
  actionItem: string

  // Section headings (shared across slides, kept here for full-slide ownership)
  trustLabel: string
  emotionLabel: string
  actionLabel: string
  revealButton: string
}

export const copy: MarketVariants<Slide3UrgencyCopy> = {
  US: {
    title: 'Fake bank alerts',
    sender: 'CHASE FRAUD ALERT:',
    threatBody:
      'Unauthorized access detected on your account. Your funds are at risk. Verify your identity within 30 minutes or your account will be permanently locked.',
    urlBefore: 'secure-',
    brandToken: 'chase',
    urlAfter: '-verify.com/urgent',
    trustItem: 'Claiming to be Chase',
    pressureFear: 'Fear: "funds are at risk", "account will be locked"',
    pressureUrgency: 'Urgency: "within 30 minutes"',
    actionItem: 'Click the link, share info',
    trustLabel: 'Gain trust',
    emotionLabel: 'Trigger emotion',
    actionLabel: 'Push action',
    revealButton: 'Reveal the pattern',
  },
  GB: {
    sender: 'HSBC FRAUD TEAM:',
    // "Unauthorized" -> "Unauthorised" auto-handled by toMarketSpelling.
    brandToken: 'hsbc',
    trustItem: 'Claiming to be HSBC',
  },
  CA: {
    sender: 'RBC FRAUD ALERT:',
    brandToken: 'rbc',
    trustItem: 'Claiming to be RBC',
  },
  AU: {
    sender: 'COMMBANK FRAUD ALERT:',
    brandToken: 'commbank',
    trustItem: 'Claiming to be CommBank',
  },
  // Qatar face: the conventions say never to name a real Qatari bank, so the
  // impersonated brand is "your bank" (a real SMS would carry the bank's own
  // name, which is exactly why the spoof works). The threat / urgency phrases
  // are unchanged so the pattern-breakdown callouts still match.
  QA: {
    sender: 'BANK FRAUD ALERT:',
    brandToken: 'bank',
    trustItem: 'Claiming to be your bank',
  },
  ar: {
    title: 'تنبيهات مصرفية مزيفة',
    sender: 'تنبيه احتيال من البنك:',
    threatBody:
      'تم رصد دخول غير مصرح به إلى حسابك. أموالك في خطر. تحقّق من هويتك خلال 30 دقيقة وإلا سيتم قفل حسابك نهائياً.',
    urlBefore: 'secure-',
    brandToken: 'bank',
    urlAfter: '-verify.com/urgent',
    trustItem: 'انتحال صفة البنك الذي تتعامل معه',
    pressureFear: 'الخوف: "أموالك في خطر"، "سيتم قفل حسابك"',
    pressureUrgency: 'الاستعجال: "خلال 30 دقيقة"',
    actionItem: 'النقر على الرابط ومشاركة المعلومات',
    trustLabel: 'كسب الثقة',
    emotionLabel: 'إثارة المشاعر',
    actionLabel: 'الدفع إلى التصرف',
    revealButton: 'اكشف النمط',
  },
}

// DCUC (military) tenant: swap the generic US bank for USAA, a military-focused
// institution that is heavily impersonated against service members and their
// families, and (unlike Navy Federal) not a DCUC member, so it's safe to use as
// the impersonated brand. The "$412 at the Commissary" detail grounds it in a
// military spending context without reusing Slide04's "the Exchange / $326".
// The threat/urgency phrases ("funds are at risk", "permanently locked",
// "within 30 minutes") are kept so the pattern-breakdown callouts still match.
const militaryUrgency: Partial<Slide3UrgencyCopy> = {
  sender: 'USAA FRAUD ALERT:',
  threatBody:
    'Unauthorized $412 charge at the Commissary detected. Your funds are at risk. Verify your identity within 30 minutes or your account will be permanently locked.',
  brandToken: 'usaa',
  trustItem: 'Claiming to be USAA',
}

export const siteCopy: SiteVariants<Slide3UrgencyCopy> = {
  dcuc: militaryUrgency,
  amba: militaryUrgency,
}
